/**
 * CreatorSummaryStats - Summary metrics display for Creator Portal
 * 
 * Displays aggregated stats across all creator's training artifacts.
 * Shows "Not enough data" when insufficient sample sizes.
 * Phase 5: Added Total Opens + Avg Behavioral Rate cards.
 */

import { Card } from "@/components/ui/card";
import { 
  BookOpen, 
  Users, 
  Building2, 
  TrendingUp, 
  Target, 
  CheckCircle2,
  Clock,
  Eye,
  Activity
} from "lucide-react";

interface SummaryStats {
  totalArtifacts: number;
  totalSessions: number;
  totalTechnicians: number;
  totalShops: number;
  avgConfidenceDelta: number | null;
  avgEfficiencyDelta: number | null;
  verifiedCompletions: number;
  likelyCompletions: number;
  totalOpens?: number;
  avgBehavioralRate?: number | null;
}

interface CreatorSummaryStatsProps {
  stats: SummaryStats;
}

interface StatCardProps {
  label: string;
  value: string | number;
  icon: React.ReactNode;
  subtitle?: string;
}

function StatCard({ label, value, icon, subtitle }: StatCardProps) {
  return (
    <Card className="p-4">
      <div className="flex items-start gap-3">
        <div className="p-2 rounded-lg bg-primary/10 text-primary">
          {icon}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm text-muted-foreground">{label}</p>
          <p className="text-2xl font-semibold mt-1">{value}</p>
          {subtitle && (
            <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>
          )}
        </div>
      </div>
    </Card>
  );
}

export function CreatorSummaryStats({ stats }: CreatorSummaryStatsProps) {
  const {
    totalArtifacts,
    totalSessions,
    totalTechnicians,
    totalShops,
    avgConfidenceDelta,
    avgEfficiencyDelta,
    verifiedCompletions,
    likelyCompletions,
    totalOpens,
    avgBehavioralRate,
  } = stats;

  // Format efficiency delta
  const efficiencyDisplay = avgEfficiencyDelta !== null
    ? `${avgEfficiencyDelta > 0 ? '+' : ''}${Math.round(avgEfficiencyDelta / 5) * 5}%`
    : '—';

  // Format confidence delta
  const confidenceDisplay = avgConfidenceDelta !== null
    ? `${avgConfidenceDelta > 0 ? '+' : ''}${Math.round(avgConfidenceDelta * 100)}%`
    : '—';

  // Format behavioral rate
  const behavioralDisplay = avgBehavioralRate !== null && avgBehavioralRate !== undefined && avgBehavioralRate > 0
    ? `${Math.round(avgBehavioralRate * 100)}%`
    : '—';

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-4 gap-4">
      <StatCard
        label="Training Artifacts"
        value={totalArtifacts}
        icon={<BookOpen className="h-5 w-5" />}
        subtitle="Linked to your account"
      />
      
      <StatCard
        label="Total Sessions"
        value={totalSessions.toLocaleString()}
        icon={<Clock className="h-5 w-5" />}
        subtitle="Training views"
      />
      
      <StatCard
        label="Total Opens"
        value={(totalOpens ?? 0) > 0 ? (totalOpens ?? 0).toLocaleString() : '—'}
        icon={<Eye className="h-5 w-5" />}
        subtitle={(totalOpens ?? 0) > 0 ? "Artifact open events" : "Not enough data"}
      />
      
      <StatCard
        label="Technicians Reached"
        value={totalTechnicians.toLocaleString()}
        icon={<Users className="h-5 w-5" />}
        subtitle="Approximate total"
      />
      
      <StatCard
        label="Shops Reached"
        value={totalShops.toLocaleString()}
        icon={<Building2 className="h-5 w-5" />}
        subtitle="Approximate total"
      />
      
      <StatCard
        label="Efficiency Impact"
        value={efficiencyDisplay}
        icon={<TrendingUp className="h-5 w-5" />}
        subtitle={avgEfficiencyDelta !== null ? "Weighted avg improvement" : "Not enough data"}
      />
      
      <StatCard
        label="Confidence Impact"
        value={confidenceDisplay}
        icon={<Target className="h-5 w-5" />}
        subtitle={avgConfidenceDelta !== null ? "Weighted avg improvement" : "Not enough data"}
      />
      
      <StatCard
        label="Avg Behavioral Rate"
        value={behavioralDisplay}
        icon={<Activity className="h-5 w-5" />}
        subtitle={avgBehavioralRate !== null && avgBehavioralRate !== undefined && avgBehavioralRate > 0 
          ? "Notes + progress rate" 
          : "Not enough data"}
      />
    </div>
  );
}

interface CompletionsSummaryProps {
  verified: number;
  likely: number;
}

export function CompletionsSummary({ verified, likely }: CompletionsSummaryProps) {
  return (
    <Card className="p-4">
      <div className="flex items-center gap-3">
        <div className="p-2 rounded-lg bg-green-500/10 text-green-600">
          <CheckCircle2 className="h-5 w-5" />
        </div>
        <div>
          <p className="text-sm text-muted-foreground">Total Completions</p>
          <div className="flex items-baseline gap-2 mt-1">
            <span className="text-2xl font-semibold">{(verified + likely).toLocaleString()}</span>
            <span className="text-sm text-muted-foreground">
              ({verified.toLocaleString()} verified, {likely.toLocaleString()} likely)
            </span>
          </div>
        </div>
      </div>
    </Card>
  );
}
